import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Badge, Table, Alert, Form } from 'react-bootstrap';
import { ArrowLeft, Calendar, DollarSign, FileText, Package, Check, X, AlertCircle } from 'lucide-react';
import { getOrderById, updateOrderStatus } from '../../api/orders';
import useAuth from '../../hooks/useAuth';
import Loading from '../common/Loading';

const OrderDetail = ({ orderId, editMode, onCancel, onSuccess }) => {
    const { isAdmin, isManager, isSalesRep, isDistributor } = useAuth();

    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [commentairerejet, setCommentairerejet] = useState('');

    const canUpdateStatus = isAdmin || isManager;

    useEffect(() => {
        const fetchOrder = async () => {
            try {
                setLoading(true);
                setError('');

                const orderData = await getOrderById(orderId);
                console.log('Fetched order data:', orderData);

                setOrder(orderData);
                setSelectedStatus(orderData.status);
                // If there's already a rejection comment, load it
                if (orderData.commentairerejet) {
                    setCommentairerejet(orderData.commentairerejet);
                }
            } catch (err) {
                setError('Failed to load order data. Please try again.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        if (orderId) {
            fetchOrder();
        }
    }, [orderId]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('fr-FR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatCurrency = (amount, withSymbol = true) => {
        if (amount === null || amount === undefined) {
            return withSymbol ? '0 FCFA' : '0';
        }
    
        const formattedValue = Number(amount)
            .toLocaleString('fr-FR') // Utilise automatiquement le point comme séparateur de milliers en français
            .replace(/\s/g, '.');    // Remplace les espaces par des points
    
        return withSymbol ? `${formattedValue} FCFA` : formattedValue;
    };
    

    const calculateSubTotal = () => {
        if (!order || !order.items) return 0;
        return order.items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
    };

    const calculateVatAmount = () => {
        const subTotal = calculateSubTotal();
        return subTotal * 0.18; // 18% TVA
    };

    const calculateTotal = () => {
        const subTotal = calculateSubTotal();
        const vatAmount = order.vatApplicable ? calculateVatAmount() : 0;
        return subTotal + vatAmount;
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'PENDING':
                return <Badge bg="warning">En attente</Badge>;
            case 'CONFIRMED':
                return <Badge bg="primary">Confirmé</Badge>;
            case 'DELIVERED':
                return <Badge bg="success">Livré</Badge>;
            case 'REJECTED':
                return <Badge bg="danger">Rejeté</Badge>;
            default:
                return <Badge bg="secondary">{status}</Badge>;
        }
    };

    // Get valid next status options based on current status
    const getValidStatusOptions = (currentStatus) => {
        switch (currentStatus) {
            case 'PENDING':
                return [
                    { value: 'PENDING', label: 'En attente' },
                    { value: 'CONFIRMED', label: 'Confirmée' },
                    { value: 'REJECTED', label: 'Rejeté' }
                ];
            case 'CONFIRMED':
                return [
                    { value: 'CONFIRMED', label: 'Confirmée' },
                    { value: 'DELIVERED', label: 'Livré' },
                    { value: 'REJECTED', label: 'Rejeté' }
                ];
            case 'DELIVERED':
                // No transitions allowed from DELIVERED
                return [
                    { value: 'DELIVERED', label: 'Livré' }
                ];
            case 'REJECTED':
                // No transitions allowed from REJECTED
                return [
                    { value: 'REJECTED', label: 'Rejeté' }
                ];
            default:
                return [];
        }
    };

    const handleStatusChange = (e) => {
        setSelectedStatus(e.target.value);
    };

    const handleRejectionCommentChange = (e) => {
        setCommentairerejet(e.target.value);
    };

    const handleUpdateStatus = async () => {
        if (selectedStatus === order.status) return;

        try {
            setUpdating(true);
            setError('');

            // Include rejection comment if status is REJECTED
            const requestData = {
                status: selectedStatus
            };

            if (selectedStatus === 'REJECTED') {
                if (!commentairerejet.trim()) {
                    setError('Veuillez fournir un motif de rejet.');
                    setUpdating(false);
                    return;
                }
                requestData.commentairerejet = commentairerejet;
            }

            await updateOrderStatus(orderId, requestData);

            // Update local order status
            setOrder({
                ...order,
                status: selectedStatus,
                ...(selectedStatus === 'REJECTED' && { commentairerejet })
            });

            setSuccess(`Le statut de la commande a été mis à jour avec succès en "${selectedStatus}" !`);

            // Call success callback after delay
            setTimeout(() => {
                if (onSuccess) onSuccess();
            }, 1500);
        } catch (err) {
            setError('Échec de la mise à jour du statut de la commande. Veuillez vérifier les stocks des produits.');
            console.error(err);
        } finally {
            setUpdating(false);
        }
    };

    if (loading) {
        return <Loading message="Chargement des détails de la commande..." />;
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    if (!order) {
        return <Alert variant="warning">Commande non trouvée</Alert>;
    }

    const validStatusOptions = getValidStatusOptions(order.status);
    const canChangeStatus = ['PENDING', 'CONFIRMED'].includes(order.status);

    // Calcul du sous-total et de la TVA
    const subTotal = calculateSubTotal();
    const vatAmount = order.vatApplicable ? calculateVatAmount() : 0;
    const totalAmount = calculateTotal();

    // Configuration - Ajustez cette URL selon votre configuration backend
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://apps.prod.atlanticds-sn.com/senoscar';

    // Fonction pour obtenir l'URL de l'image principale d'un produit
    const getMainImageUrl = (product) => {
        if (!product.images || product.images.length === 0) {
            return null;
        }

        const mainImage = product.images.find(img => img.mainImage);
        const path = mainImage ? mainImage.path : product.images[0].path;

        // Traiter l'URL correctement selon le format stocké en base
        if (path.startsWith('http://') || path.startsWith('https://')) {
            return path;
        }

        // // Si le chemin commence par "products/"
        // if (path.startsWith('products/')) {
        //     // Garder le chemin tel quel car le FileController est mappé sur /products/...
        //     return `${API_BASE_URL}/${path}`;
        // }

        // Par sécurité, si le format est différent
        return `${API_BASE_URL}/uploads/${path}`;
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <Button
                    variant="outline-secondary"
                    onClick={onCancel}
                >
                    <ArrowLeft size={16} className="me-1" />
                    Retour aux commandes
                </Button>

                {success && <Alert variant="success" className="mb-0 py-2 px-3">{success}</Alert>}
            </div>

            <Row className="mb-4">
                <Col lg={8}>
                    <Card className="shadow-sm mb-4">
                        <Card.Header className="bg-white">
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">Commande #{order.orderNumber}</h5>
                                {getStatusBadge(order.status)}
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <div className="mb-3">
                                        <div className="text-muted mb-1">Client</div>
                                        <div className="fw-bold">{order.partner.name}</div>
                                        <small className="text-muted">
                                            {order.partner.partnerType === 'DISTRIBUTOR' ? 'Distributeur' : 'Détaillant'}
                                        </small>
                                    </div>

                                    <div>
                                        <div className="text-muted mb-1">Statut TVA</div>
                                        <div>
                                            {order.vatApplicable ? (
                                                <Badge bg="info">Avec TVA</Badge>
                                            ) : (
                                                <Badge bg="warning">Exonéré de TVA</Badge>
                                            )}
                                        </div>
                                    </div>
                                </Col>

                                <Col md={6}>
                                    <div className="mb-3 d-flex align-items-start">
                                        <Calendar size={18} className="me-2 text-muted mt-1" />
                                        <div>
                                            <div className="text-muted">Date commande</div>
                                            <div>{formatDateTime(order.orderDate)}</div>
                                        </div>
                                    </div>

                                    {order.deliveryDate && (
                                        <div className="d-flex align-items-start">
                                            <Package size={18} className="me-2 text-muted mt-1" />
                                            <div>
                                                <div className="text-muted">Date de livraison</div>
                                                <div>{formatDate(order.deliveryDate)}</div>
                                            </div>
                                        </div>
                                    )}

                                    {order.status === 'REJECTED' && (
                                        <div className="mt-3">
                                            <div className="text-muted mb-1">Motif de rejet</div>
                                            <div className="p-2 bg-light rounded">
                                                {order.commentairerejet || 'Aucun motif fourni'}
                                            </div>
                                        </div>
                                    )}
                                </Col>
                            </Row>

                            {order.deliveryAddress && (
                                <div className="mb-4">
                                    <div className="text-muted mb-1">Adresse de livraison</div>
                                    <div>{order.deliveryAddress}</div>
                                </div>
                            )}

                            {order.notes && (
                                <div className="mb-4">
                                    <div className="text-muted mb-1">Notes</div>
                                    <div className="p-3 bg-light rounded">
                                        {order.notes}
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    <Card className="shadow-sm">
                        <Card.Header className="bg-white">
                            <h5 className="mb-0">Produits de la commande</h5>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table responsive className="mb-0">
                                <thead>
                                    <tr>
                                        <th>Produit</th>
                                        <th className="text-center">Quantité</th>
                                        <th className="text-end">Prix Unitaire</th>
                                        <th className="text-end">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {order.items && order.items.map((item, index) => (
                                        <tr key={index}>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    {getMainImageUrl(item.product) ? (
                                                        <div
                                                            style={{
                                                                width: '50px',
                                                                height: '50px',
                                                                backgroundImage: `url("${getMainImageUrl(item.product)}")`,
                                                                backgroundSize: 'cover',
                                                                backgroundPosition: 'center',
                                                                borderRadius: '4px',
                                                                marginRight: '8px'
                                                            }}
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.style.backgroundImage = 'none';
                                                                e.target.style.display = 'flex';
                                                                e.target.style.justifyContent = 'center';
                                                                e.target.style.alignItems = 'center';
                                                                e.target.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="20" rx="2"/></svg>';
                                                            }}
                                                        />
                                                    ) : (
                                                        <div
                                                            className="d-flex justify-content-center align-items-center bg-light"
                                                            style={{
                                                                width: '50px',
                                                                height: '50px',
                                                                borderRadius: '4px',
                                                                marginRight: '8px'
                                                            }}
                                                        >
                                                            <Package size={20} className="text-muted" />
                                                        </div>
                                                    )}
                                                    <div>
                                                        <div className="fw-semibold">{item.product.name}</div>
                                                        <small className="text-muted">{item.product.sku}</small>
                                                    </div>
                                                </div>
                                            </td>

                                            <td className="text-center">{item.quantity}</td>

                                            <td className="text-end">{formatCurrency(item.unitPrice)}</td>
                                            <td className="text-end">{formatCurrency(item.quantity * item.unitPrice)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="border-top">
                                    <tr>
                                        <td colSpan="3" className="text-end fw-bold">Sous total:</td>
                                        <td className="text-end">{formatCurrency(subTotal)}</td>
                                    </tr>
                                    {order.vatApplicable && (
                                        <tr>
                                            <td colSpan="3" className="text-end">TVA (18%):</td>
                                            <td className="text-end">{formatCurrency(vatAmount)}</td>
                                        </tr>
                                    )}
                                    <tr>
                                        <td colSpan="3" className="text-end fw-bold">Total:</td>
                                        <td className="text-end fw-bold">{formatCurrency(totalAmount)}</td>
                                    </tr>
                                </tfoot>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={4}>
                    <Card className="shadow-sm mb-4">
                        <Card.Header className="bg-white">
                            <h5 className="mb-0">Statut commande</h5>
                        </Card.Header>
                        <Card.Body>
                            {canUpdateStatus ? (
                                <div>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Statut actuel</Form.Label>
                                        <div className="mb-3">
                                            {getStatusBadge(order.status)}
                                        </div>

                                        {canChangeStatus && (
                                            <>
                                                <Form.Label>Modifier Statut</Form.Label>
                                                <Form.Select
                                                    value={selectedStatus}
                                                    onChange={handleStatusChange}
                                                    disabled={updating || !canChangeStatus}
                                                >
                                                    {validStatusOptions.map(option => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </>
                                        )}
                                    </Form.Group>

                                    {selectedStatus === 'REJECTED' && order.status !== 'REJECTED' && (
                                        <Form.Group className="mb-3">
                                            <Form.Label>Motif de rejet</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                value={commentairerejet}
                                                onChange={handleRejectionCommentChange}
                                                placeholder="Veuillez indiquer le motif du rejet"
                                                required
                                            />
                                        </Form.Group>
                                    )}

                                    {canChangeStatus && (
                                        <div className="d-grid mt-3">
                                            <Button
                                                variant="primary"
                                                onClick={handleUpdateStatus}
                                                disabled={updating || selectedStatus === order.status}
                                            >
                                                {updating ? (
                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                ) : (
                                                    <>
                                                        <Check size={16} className="me-1" />
                                                        Modifier
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    )}

                                    {!canChangeStatus && (
                                        <Alert variant="warning" className="mt-3">
                                            <AlertCircle size={16} className="me-1" />
                                            Cette commande ne peut plus être modifiée.
                                        </Alert>
                                    )}

                                    {order.status === 'DELIVERED' && (
                                        <Alert variant="info" className="mt-3">
                                            <AlertCircle size={16} className="me-1" />
                                            Le stock a été ajusté pour cette commande
                                        </Alert>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-3">
                                    <div className="mb-3">
                                        {getStatusBadge(order.status)}
                                    </div>
                                    {order.status === 'PENDING' &&
                                        <p className="text-muted mb-0">
                                            Merci pour votre commande. Veuillez patienter, SOPLAST GROUP va procéder à la validation de votre commande.
                                        </p>
                                    }
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Informations supplémentaires sur la commande si nécessaire */}
                    {order.status === 'DELIVERED' && (
                        <Card className="shadow-sm mb-4">
                            <Card.Header className="bg-white">
                                <h5 className="mb-0">Informations de livraison</h5>
                            </Card.Header>
                            <Card.Body>
                                <div className="d-flex align-items-start mb-3">
                                    <Calendar size={18} className="me-2 text-muted mt-1" />
                                    <div>
                                        <div className="text-muted">Date de livraison</div>
                                        <div>{formatDate(order.deliveryDate)}</div>
                                    </div>
                                </div>

                                {order.deliveryAddress && (
                                    <div className="d-flex align-items-start">
                                        <Package size={18} className="me-2 text-muted mt-1" />
                                        <div>
                                            <div className="text-muted">Adresse de livraison</div>
                                            <div>{order.deliveryAddress}</div>
                                        </div>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    )}
                </Col>
            </Row>
        </div>
    );
};

export default OrderDetail;